import { useTranslation } from 'react-i18next';
import { Badge, Col, Row } from 'reactstrap';
import { Button, Icon } from '@octano/global-ui';

import SectionTitle from '../../../../../../components/text/SectionTitle';
import { TranslationsKeys } from '../../../../../../locales/translations';
import {
  GroupedDocumentationItem,
  RequestedDocument,
} from '../../../../../../types/signDocumentationTypes';
import isIcon from '../../../../../../utils/isIcon';

interface Props {
  requestedDocuments: RequestedDocument[];
  groupedDocuments?: GroupedDocumentationItem[];
  postulationDetailId: number;
  onDownload: (doc: RequestedDocument) => void;
  onDownloadAll: () => void;
}

export default function DocumentationSection({
  requestedDocuments,
  groupedDocuments,
  onDownload,
  onDownloadAll,
}: Props) {
  const { t } = useTranslation(
    TranslationsKeys.HOMOLOGATION_AUTHORIZATION_DETAIL,
  );

  const renderTile = (reqDoc: RequestedDocument) => {
    const doc = reqDoc.document;
    return (
      <Col key={doc.id} xs={6} md={2} className="mb-3">
        <div className="d-flex align-items-end">
          <span className="text-uppercase fs-14 fw-600 text-secondary">
            {doc.name}
          </span>
        </div>

        <div
          className="bg-light px-2 py-2 mb-2 d-flex flex-column align-items-center justify-content-center"
          style={{ minHeight: '120px' }}
        >
          <Icon
            name={isIcon(doc.icon) ? doc.icon : 'certificate'}
            size="55px"
            color="secondary"
          />
          {reqDoc.uploaded && (
            <div className="mt-2 w-100 px-1">
              <Button
                icon="download"
                onClick={() => onDownload(reqDoc)}
                outlined
                size="sm"
                text={t('documentation.download')}
                fullwidth
              />
            </div>
          )}
        </div>
      </Col>
    );
  };

  const renderGroup = (
    group: Extract<GroupedDocumentationItem, { kind: 'group' }>,
  ) => {
    const ruleText =
      group.minOptionalRequired && group.minOptionalRequired > 0
        ? t('documentation.group.ruleMinOptional', {
            count: group.minOptionalRequired,
          })
        : t('documentation.group.ruleAllRequired');

    return (
      <Col key={group.groupCode} xs={12} className="mb-3">
        <div className="d-flex justify-content-between align-items-start flex-wrap mb-1">
          <div>
            <span className="fs-14 fw-600 text-secondary">{group.name}</span>
            {group.hint && (
              <p className="text-muted mb-0 fs-13">{group.hint}</p>
            )}
            <p className="text-muted mb-0 fs-13">{ruleText}</p>
          </div>
          <Badge
            pill
            className="fs-13 fw-400 py-1 px-3 text-dark"
            style={{
              backgroundColor: group.satisfied ? '#CEF0DA' : '#FDDFDF',
            }}
          >
            {group.satisfied
              ? t('documentation.group.satisfied')
              : t('documentation.group.missing')}
          </Badge>
        </div>
        <Row className="mx-n2 align-items-end">
          {group.documents.map((childDoc) => renderTile(childDoc))}
        </Row>
      </Col>
    );
  };

  const groupedItems =
    groupedDocuments && groupedDocuments.length > 0 ? groupedDocuments : null;
  const isEmpty = groupedItems ? false : requestedDocuments.length === 0;

  return (
    <div className="mb-4">
      <div className="d-flex justify-content-between align-items-start mb-1">
        <SectionTitle text={t('documentation.title')} />
        <Button
          text={t('documentation.downloadAll')}
          outlined
          size="sm"
          onClick={onDownloadAll}
          icon="download"
        />
      </div>
      <p className="text-muted mb-3">{t('documentation.description')}</p>

      {isEmpty ? (
        <p className="text-muted text-center py-3">
          {t('documentation.noDocuments')}
        </p>
      ) : (
        <Row className="mx-n2 align-items-end">
          {groupedItems
            ? groupedItems.map((entry) =>
                entry.kind === 'group'
                  ? renderGroup(entry)
                  : renderTile(entry.item),
              )
            : requestedDocuments.map((reqDoc) => renderTile(reqDoc))}
        </Row>
      )}
    </div>
  );
}
